import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  ButtonProps,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { useAppSelector } from '../../../hooks';
import { TP_COLORS } from '../../../constants';
import { getCurriculumsV2 } from '../../../store/V2/CurriculumSlice';
import { getCountry, getUrlConvertedString } from '../../../helper';
import {
  getTutors,
  loadMoreTutors,
  TGetTutors,
} from '../../../store/V2/TutorSlice/api';
import { getTutorsState } from '../../../store/V2/TutorSlice';
import FilterSection from './components/FilterSection';
import TutorCard from './components/TutorCard';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import TutorsLoader from './components/TutorsLoader';

const getQueryParams = (newState: TState) => {
  const paramsData: Record<string, string> = {};

  if (newState.selectedCurriculum) {
    paramsData.curriculum = getUrlConvertedString(
      newState.selectedCurriculum.value
    );
  }
  if (newState.selectedSubject) {
    paramsData.subject = getUrlConvertedString(newState.selectedSubject.value);
  }
  if (newState.selectedGrade) {
    paramsData.grade = getUrlConvertedString(newState.selectedGrade.value);
  }
  const queryParams = new URLSearchParams(paramsData).toString();

  return queryParams;
};

const buttonProps: ButtonProps = {
  disableElevation: true,
  variant: 'contained',
  color: 'secondary',
  sx: {
    borderRadius: '25px',
    backgroundColor: TP_COLORS.gray.darken,
  },
  endIcon: <KeyboardArrowDownIcon />,
};

type TSelectOption = {
  label: string;
  value: string;
};

export type TState = {
  selectedCurriculum: TSelectOption | null;
  selectedSubject: TSelectOption | null;
  selectedGrade: TSelectOption | null;
  submitClicked?: boolean;
};

const initialState: TState = {
  selectedCurriculum: null,
  selectedSubject: null,
  selectedGrade: null,
  submitClicked: false,
};

const Tutors: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const fromOnboarding = urlParams.has('fromOnboarding');

  // Store
  const { status, tutorsList } = useAppSelector(getTutorsState);

  // State
  const [state, setState] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(getCurriculumsV2());
    if (fromOnboarding) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (!location.search) {
      dispatch(getTutors(getApiData(state)));
    }
  }, [location.search]);

  const handleNavigate = (newState: TState) => {
    const queryParams = getQueryParams(newState);

    const url = `${process.env.PUBLIC_URL}/portal/tutors${
      queryParams ? `?${queryParams}` : ''
    }`;

    dispatch(getTutors(getApiData(newState)));

    navigate(url);
  };

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => {
      const newState = {
        ...prevState,
        ...attributes,
      };

      handleNavigate(newState);

      return newState;
    });
  };

  const getApiData = (newState, overrideOffset?: number): TGetTutors => {
    const { selectedCurriculum, selectedSubject, selectedGrade } = newState;

    const apiData: TGetTutors = {
      limit: 12,
      offset: overrideOffset ?? 0,
      country: getCountry(),
    };

    if (selectedCurriculum) {
      apiData.curriculum = selectedCurriculum.value;
    }
    if (selectedSubject) {
      apiData.subject = selectedSubject.value;
    }
    if (selectedGrade) {
      apiData.grade = selectedGrade.value;
    }

    return apiData;
  };

  const handleLoadMore = () => {
    if (tutorsList?.list.length) {
      const offset = tutorsList.list.length;
      dispatch(loadMoreTutors(getApiData(state, offset)));
    }
  };

  const handleTutorSelect = (tutorId: string) => {
    const queryParams = getQueryParams(state);

    navigate(
      `${process.env.PUBLIC_URL}/portal/tutors/${tutorId}?${queryParams}`
    );
  };

  return (
    <>
      <Box display={isLoading ? 'block' : 'none'}>
        <TutorsLoader />
      </Box>
      <Box
        display={isLoading ? 'none' : 'block'}
        sx={{
          paddingBottom: '64px',
          background: `url('${process.env.PUBLIC_URL}/images/tutorProfile/bg-grid-new.png') 0px -16px repeat-x, linear-gradient(to right, #FFFFFF -2.82%, #DAF4F3 100.08%)`,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            alignItems="center"
            textAlign="center"
            spacing={1}
            paddingTop="80px"
            paddingBottom="64px"
          >
            <Typography
              variant="h3"
              fontWeight="800"
              color={TP_COLORS.gray.darken}
            >
              Level Up With the
              <span style={{ color: TP_COLORS.tutopiyaGreenMain.darken }}>
                {' '}
                Right Tutor
              </span>
            </Typography>

            <Typography
              fontSize="20px"
              color={TP_COLORS.gray.darken}
              maxWidth="600px"
            >
              Explore top-rated tutors tailored to your subject, level, and
              goals. Learning just got a lot more personal.
            </Typography>
          </Stack>

          <FilterSection
            state={state}
            updateStateAttributes={updateStateAttributes}
          />

          <Grid container spacing="20px" marginTop="20px">
            {tutorsList?.list.length
              ? tutorsList.list.map((tutorItem) => (
                  <Grid key={tutorItem._id} item xs={12} sm={6} md={4} lg={4}>
                    <TutorCard
                      tutorData={tutorItem}
                      handleSelect={handleTutorSelect}
                    />
                  </Grid>
                ))
              : null}
          </Grid>

          {!tutorsList ? (
            <BasicLoader
              containerProps={{
                minHeight: '300px',
                alignItems: 'center',
              }}
            />
          ) : null}

          {tutorsList && (tutorsList.totalDocs || 0) > tutorsList.list.length && (
            <Stack justifyContent="center" alignItems="center" marginTop="40px">
              <LoadingButton
                {...buttonProps}
                loading={status === 'loading'}
                disabled={status === 'loading'}
                onClick={handleLoadMore}
              >
                See more
              </LoadingButton>
            </Stack>
          )}

          {tutorsList &&
          tutorsList.list &&
          tutorsList.list.length < 1 &&
          status === 'idle' ? (
            <Stack
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
            >
              <Typography variant="h6">No Tutors found</Typography>
            </Stack>
          ) : null}
        </Container>
      </Box>
    </>
  );
};

export default Tutors;
